import React from 'react';

const BotIntegrationForm = ({ value, onChange, onFetch, fetching = false, onTest, canTest = false, testing = false }) => {
  const update = (patch) => onChange({ ...value, ...patch });
  const registered = Boolean(value?.configured || value?.credentialAvailable);
  const status = value?.configured
    ? `${value.credentialStatus || 'configured'} · credential version ${value.credentialVersion || 1}`
    : value?.credentialAvailable
    ? `${value.credentialStatus || 'registered'} · credential version ${value.credentialVersion || 1} (not yet attached to this tool)`
    : 'Not configured';

  return (
    <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '2px solid var(--garage-border-2)' }}>
      <h4 style={{ margin: '0 0 4px', fontSize: '16px', color: 'var(--garage-text)' }}>
        RingCentral Bot Integration
      </h4>
      <p style={{ margin: '0 0 16px', color: 'var(--garage-text-3)', fontSize: '13px' }}>
        Status: {status}. AI Garage automatically reuses the encrypted credential registered for this bot extension. Secrets are never displayed after saving.
      </p>

      <div
        style={{
          margin: '0 0 18px', padding: '14px 16px', border: '1px solid var(--garage-border-2)',
          borderRadius: '10px', background: 'var(--garage-surface-2, #fafafa)',
        }}
      >
        <h5 style={{ margin: '0 0 8px', fontSize: '14px', color: 'var(--garage-text)' }}>
          New bot onboarding
        </h5>
        <ol style={{ margin: 0, paddingLeft: '20px', color: 'var(--garage-text-2)', fontSize: '13px', lineHeight: 1.65 }}>
          <li>Enter the bot extension ID/number or bot email.</li>
          <li>Click <strong>Fetch registered bot</strong> to use credentials provisioned by the bot deployment.</li>
          <li>Configure Web/RC-app launch, then click <strong>Test RingCentral connection before saving</strong>.</li>
          <li>Click <strong>Update Tool</strong> only after the connection test succeeds.</li>
        </ol>
        {!registered && !value?.provideCredentials && (
          <p style={{ margin: '10px 0 0', color: 'var(--garage-text-3)', fontSize: '12px', lineHeight: 1.5 }}>
            If Fetch finds no record, ask the bot owner to run its deployment provisioning step. Only an authorized admin should use the secure credential fallback below.
          </p>
        )}
      </div>

      <div className="garage-form-grid">
        <div className="garage-form-group">
          <label>Bot extension ID or number *</label>
          <input
            type="text"
            value={value.botExtensionId}
            onChange={(e) => update({ botExtensionId: e.target.value })}
            placeholder="18598 or immutable API extension ID"
          />
        </div>
        <div className="garage-form-group">
          <label>Bot email *</label>
          <input
            type="email"
            value={value.botEmail}
            onChange={(e) => update({ botEmail: e.target.value })}
            placeholder="bot@ringcentral.com"
          />
        </div>
        <div className="garage-form-group">
          <label>RingCentral server URL *</label>
          <input
            type="url"
            value={value.serverUrl}
            onChange={(e) => update({ serverUrl: e.target.value })}
            placeholder="https://platform.ringcentral.com"
          />
        </div>
        <div className="garage-form-group">
          <label>Web URL template</label>
          <input
            type="text"
            value={value.webUrlTemplate}
            onChange={(e) => update({ webUrlTemplate: e.target.value })}
            placeholder="https://app.ringcentral.com/l/messages/{conversationId}"
          />
        </div>
        <div className="garage-form-group">
          <label>RC app URL template</label>
          <input
            type="text"
            value={value.rcAppUrlTemplate}
            onChange={(e) => update({ rcAppUrlTemplate: e.target.value })}
            placeholder="Validated client URI with {conversationId}"
          />
        </div>
        <div className="garage-form-group">
          <label>Fallback URL</label>
          <input
            type="url"
            value={value.fallbackUrl}
            onChange={(e) => update({ fallbackUrl: e.target.value })}
            placeholder="https://... (optional)"
          />
        </div>
      </div>

      <button
        type="button"
        className="garage-btn garage-btn-secondary"
        onClick={onFetch}
        disabled={fetching || !((value.botExtensionId || '').trim() || (value.botEmail || '').trim())}
        style={{ marginTop: '12px' }}
      >
        {fetching ? 'Fetching registered bot…' : 'Fetch registered bot'}
      </button>
      <p style={{ margin: '8px 0 0', color: 'var(--garage-text-3)', fontSize: '12px' }}>
        Enter the bot extension or email, then fetch. This reads only safe metadata; credentials remain encrypted and are never shown in the browser.
      </p>

      <p style={{ margin: '18px 0 10px', color: 'var(--garage-text-3)', fontSize: '13px' }}>
        If this extension has already been registered in this environment, no credentials are needed here. For a new bot or a credential rotation, an authorized admin can provide them once below; they are tested first, KMS-encrypted in Firestore on save, and then reused by future onboarding.
      </p>
      <label style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '14px', fontSize: '13px', color: 'var(--garage-text-2)' }}>
        <input
          type="checkbox"
          checked={Boolean(value.provideCredentials)}
          onChange={(e) => update({ provideCredentials: e.target.checked, credentials: e.target.checked ? (value.credentials || {}) : {} })}
        />
        Provide or rotate bot credentials securely
      </label>
      {value.provideCredentials && (
        <div className="garage-form-grid" style={{ marginBottom: '14px' }}>
          <div className="garage-form-group">
            <label>Client ID</label>
            <input type="password" autoComplete="off" value={value.credentials?.clientId || ''} onChange={(e) => update({ credentials: { ...(value.credentials || {}), clientId: e.target.value } })} />
          </div>
          <div className="garage-form-group">
            <label>Client secret</label>
            <input type="password" autoComplete="new-password" value={value.credentials?.clientSecret || ''} onChange={(e) => update({ credentials: { ...(value.credentials || {}), clientSecret: e.target.value } })} />
          </div>
          <div className="garage-form-group">
            <label>Access / OAuth token</label>
            <input type="password" autoComplete="off" value={value.credentials?.accessToken || ''} onChange={(e) => update({ credentials: { ...(value.credentials || {}), accessToken: e.target.value } })} />
          </div>
          <div className="garage-form-group">
            <label>Refresh token (optional)</label>
            <input type="password" autoComplete="off" value={value.credentials?.refreshToken || ''} onChange={(e) => update({ credentials: { ...(value.credentials || {}), refreshToken: e.target.value } })} />
          </div>
          <div className="garage-form-group">
            <label>JWT token (optional)</label>
            <input type="password" autoComplete="off" value={value.credentials?.jwtToken || ''} onChange={(e) => update({ credentials: { ...(value.credentials || {}), jwtToken: e.target.value } })} />
          </div>
        </div>
      )}
      <div className="garage-form-grid">
        <div className="garage-form-group">
          <label>Integration</label>
          <select value={value.enabled ? 'enabled' : 'disabled'} onChange={(e) => update({ enabled: e.target.value === 'enabled' })}>
            <option value="enabled">Enabled</option>
            <option value="disabled">Disabled</option>
          </select>
        </div>
        <div className="garage-form-group">
          <label>Web launch</label>
          <select value={value.webLaunchEnabled ? 'enabled' : 'disabled'} onChange={(e) => update({ webLaunchEnabled: e.target.value === 'enabled' })}>
            <option value="enabled">Enabled</option>
            <option value="disabled">Disabled</option>
          </select>
        </div>
        <div className="garage-form-group">
          <label>RC app launch</label>
          <select value={value.rcAppLaunchEnabled ? 'enabled' : 'disabled'} onChange={(e) => update({ rcAppLaunchEnabled: e.target.value === 'enabled' })}>
            <option value="disabled">Disabled</option>
            <option value="enabled">Enabled</option>
          </select>
        </div>
      </div>
      {canTest && (
        <button
          type="button"
          className="garage-btn garage-btn-secondary"
          onClick={onTest}
          disabled={testing}
          style={{ marginTop: '12px' }}
        >
          {testing ? 'Testing connection…' : 'Test RingCentral connection before saving'}
        </button>
      )}
    </div>
  );
};

export default BotIntegrationForm;
